/**
 * GraphExplorer Constants and Default Configurations
 *
 * This file centralizes all constant values and default configurations
 * used throughout the GraphExplorer section.
 */

import { ResourceType, SeedStatus } from '@/types';
import { StorageKey } from '@/utils/storage/useStorage.util';

import type {
  AssetFilters,
  Filters,
  GraphEntityType,
  SeedFilter,
} from './types';

/**
 * Default filter configuration for Asset entity type
 * Applied when GraphExplorer first loads or when switching to Asset view
 */
export const DEFAULT_ASSET_FILTERS: AssetFilters = {
  advancedFilters: {},
} as const;

/**
 * Default filter configuration for Seed entity type
 * Applied when switching to Seed view in GraphExplorer
 */
export const DEFAULT_SEED_FILTERS: SeedFilter = {
  type: 'domain',
  advancedFilters: {
    status: [SeedStatus.Accept],
  },
} as const;

/**
 * Default seed type for seed filters
 */
export const DEFAULT_SEED_TYPE = 'domain' as const;

/**
 * Get default filters for a specific entity type
 * @param entityType - The entity type to get defaults for
 * @returns Default filter configuration for the entity type
 */
export function getDefaultFilters(entityType: GraphEntityType): Filters {
  return entityType === ResourceType.Seed
    ? DEFAULT_SEED_FILTERS
    : DEFAULT_ASSET_FILTERS;
}

/**
 * Get storage key for a specific entity type
 * @param entityType - The entity type to get storage key for
 * @returns Storage key for persisting filters
 */
export function getStorageKey(entityType: GraphEntityType): string {
  return entityType === ResourceType.Seed
    ? `${StorageKey.FILTERS_SEEDS}.v1`
    : StorageKey.FILTERS_GRAPH_EXPLORER;
}

/**
 * Get query key for a specific entity type
 * @param entityType - The entity type to get query key for
 * @returns Query key for React Query state
 */
export function getQueryKey(entityType: GraphEntityType): string {
  return entityType === ResourceType.Seed
    ? 'seedState'
    : 'graphExplorerFilters';
}
